import * as React from 'react';
import * as DateFns from 'date-fns';
import GuideElement, {
  convertUTCtoLocalTime,
  initialDate,
  Station
} from './GuideElement';
import '../css/channel-guide.sass';
import ChannelBugView from './ChannelBugView';

interface ChannelGuideDetails {
  callLetter: string;
  imgName: string;
  stations: Station[];
  number?: string;
}

interface ChannelGuideProps {
  details: ChannelGuideDetails[];
  theme?: 'night' | 'day';
  timelineLimitHours?: number;
}

const shiftColumnFocus = (
  index: number,
  currentNode: any,
  scrollParent: any
) => {
  const focusables = document.querySelectorAll('.guide-element');
  const nextNode: any = focusables[index];
  if (nextNode) {
    nextNode.focus();
    scrollParent.scrollTo(nextNode.offsetLeft - scrollParent.offsetLeft, 0);
    currentNode.blur();
  }
};

const shiftRowFocus = (currentNode: any, value: number, scrollParent: any) => {
  const currentRow = currentNode
    .getAttribute('id')
    .replace('guide-element', '')
    .split('')[0];
  const nextRowElm = document.getElementById(
    `guide-element${parseInt(currentRow, 10) + value}0`
  );
  if (nextRowElm) {
    scrollParent.scrollTo(nextRowElm.offsetLeft - scrollParent.offsetLeft, 0);
    nextRowElm.focus();
    currentNode.blur();
  }
};

export default function ChannelGuide(props: ChannelGuideProps) {
  const { theme = 'day', timelineLimitHours = 4 } = props;
  const timeLineArr = [];
  const timeLineStartsAt = initialDate();
  const timeLineEndsAt = initialDate();
  timeLineEndsAt.setHours(timeLineStartsAt.getHours() + timelineLimitHours);
  let currentTime = timeLineStartsAt;
  let keyIndex = 0;

  for (let i = 0; i < (timelineLimitHours * 60) / 30 + 1; i++) {
    keyIndex += 1;
    timeLineArr.push(
      <div
        key={`${currentTime
          .toString()
          .toLowerCase()
          .replace(' ', '')}${keyIndex}`}
        className="milestone-value"
      >
        {DateFns.format(currentTime, 'hh:mma')}
      </div>
    );
    currentTime = DateFns.addMinutes(currentTime, 30);
  }

  const onGuideElementKeyDown = (event: any) => {
    const focusables = document.querySelectorAll('.guide-element');
    const scrollParent: any = document.querySelector('.channel-guide');
    let currentIndex = 0;
    if (focusables.length) {
      focusables.forEach((node: any, index: number) => {
        if (node.getAttribute('id') === event.target.id) {
          currentIndex = index;
        }
      });
    }
    if (event.key === 'ArrowRight')
      shiftColumnFocus(currentIndex + 1, event.target, scrollParent);
    if (event.key === 'ArrowLeft')
      shiftColumnFocus(currentIndex - 1, event.target, scrollParent);
    if (event.key === 'ArrowUp') shiftRowFocus(event.target, -1, scrollParent);
    if (event.key === 'ArrowDown') shiftRowFocus(event.target, 1, scrollParent);
    if (event.key === 'Tab') {
      let nextNode: any = focusables[currentIndex + 1];
      if (event.shiftKey) {
        nextNode = focusables[currentIndex - 1];
      }
      if (nextNode)
        scrollParent.scrollTo(nextNode.offsetLeft - scrollParent.offsetLeft, 0);
    }
  };

  const filteredStations = (stations: Station[]) => {
    const lastDateToShow = DateFns.subMinutes(timeLineEndsAt, 10);
    return stations.filter(station => {
      return (
        convertUTCtoLocalTime(station.startTime).getTime() <
        lastDateToShow.getTime()
      );
    });
  };

  return (
    <div className={`channel-guide ${theme}`}>
      <div className="timeline">{timeLineArr}</div>
      {props.details.map((channel, index) => {
        keyIndex += 1;
        return (
          <div
            className="guide-row"
            key={`${channel.imgName.toLowerCase().replace(' ', '')}${keyIndex}`}
          >
            <ChannelBugView
              callLetter={channel.callLetter}
              imgName={channel.imgName}
              number={channel.number}
            />
            <div className="guide-flex">
              {filteredStations(channel.stations).map((station, idx) => {
                keyIndex += 1;
                return (
                  <div
                    className="station"
                    key={`${station.name
                      .toLowerCase()
                      .replace(' ', '')}${keyIndex}`}
                  >
                    <GuideElement
                      cellPosition={`${index}${idx}`}
                      title={station.name}
                      startTime={station.startTime}
                      endTime={station.endTime}
                      prevSlot={channel.stations[idx - 1]}
                      onGuideElementKeyDown={(e: any) =>
                        onGuideElementKeyDown(e)
                      }
                      timeLineEndDate={timeLineEndsAt}
                      channelName={channel.callLetter}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
